import React, { useMemo } from 'react';

import { CreateNonFinancialDriverView } from '@futrli/views/views/CreateNonFinancialDriverView/CreateNonFinancialDriverView';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { useExcelFilesView } from '@futrli/views/hooks/useExcelFilesView';

import { useFormulaDriverForm } from '../../hooks/useFormulaDriverForm/useFormulaDriverForm';
import { useFreestyleDriverForm } from '../../hooks/useFreestyleDriverForm/useFreestyleDriverForm';
import { useOrgRouteParams } from '../../../hooks/useOrgRouteParams/useOrgRouteParams';
import { useSharedDriverForm } from '../../hooks/useSharedDriverForm/useSharedDriverForm';

export interface DriverBuilderContextProps {
  children?: React.ReactNode;
  driverId?: string;
}

export interface DriverBuilderContextValues {
  excelView: ReturnType<typeof useExcelFilesView>;
  formulaForm: ReturnType<typeof useFormulaDriverForm>;
  freestyleForm: ReturnType<typeof useFreestyleDriverForm>;
  organisationId: string;
  sharedForm: ReturnType<typeof useSharedDriverForm>;
  view: CreateNonFinancialDriverView;
  workspaceId: string;
}

export const DriverBuilderContext = React.createContext({} as DriverBuilderContextValues);

export const DriverBuilderContextProvider = ({ children, driverId }: DriverBuilderContextProps) => {
  const { workspaceId, organisationId } = useOrgRouteParams();

  const view = useApiView(
    useMemo(() => new CreateNonFinancialDriverView(workspaceId, organisationId, driverId), [
      workspaceId,
      organisationId,
      driverId
    ])
  );
  const { formulaDriver, freestyleDriver, financialConfig, groups } = view.state;

  const excelView = useExcelFilesView(workspaceId, organisationId, { entityType: 'driver' });

  const formulaForm = useFormulaDriverForm(formulaDriver);
  const freestyleForm = useFreestyleDriverForm(freestyleDriver, financialConfig);
  const sharedForm = useSharedDriverForm(freestyleDriver || formulaDriver, groups);

  return (
    <DriverBuilderContext.Provider
      value={{
        excelView,
        formulaForm,
        freestyleForm,
        organisationId,
        sharedForm,
        view,
        workspaceId
      }}
    >
      {children}
    </DriverBuilderContext.Provider>
  );
};

export const useDriverBuilderContext = () => React.useContext(DriverBuilderContext);
